import { ARENA } from '../../../config/constants.js';

/**
 * The Verse — the boss whose escalation the player writes (Act III-5,
 * ACT3-BIBLE §3).
 *
 * At each phase transition the fight stops and offers a three-card hand: three
 * ways it might grow, all of them bad for you, one of them less so. The cards
 * are honest — each names its trade. Refusal is a choice of verse too, and the
 * worst one, so declining takes the card the fight most wants.
 *
 * No fight in the game has ever given the player authority over the boss.
 * Ascension modifies runs pre-start and globally; this is in-fight, local, and
 * reversible only by beating the fight.
 *
 * The picks live on the boss (`boss.versePicks`) rather than in here, because
 * they belong to the fight: patterns/Composition.js reads them to shape its
 * volleys, and anything else that wants to answer them can. This system owns
 * the offering, and the one pick that is an arena rule rather than a volley —
 * `narrow`, the shrinking arena, whose hazard ring it maintains itself.
 *
 * Late phases offer `unwrite`, which removes an earlier pick at the price of
 * taking a new one — so the fight has an endgame of un-writing.
 */

const CARDS = {
  denser: {
    id: 'denser',
    label: 'Fuller Voicing',
    trade: 'Its fans carry far more bullets.',
  },
  tracking: {
    id: 'tracking',
    label: 'Attentive Setting',
    trade: 'Its bullets follow you.',
  },
  swift: {
    id: 'swift',
    label: 'Quicker Tempo',
    trade: 'Its bullets fly much faster.',
  },
  narrow: {
    id: 'narrow',
    label: 'Tighter Margins',
    trade: 'The singable floor shrinks, and the edge burns.',
  },
};

export class VerseSystem {
  constructor(eventBus, config, { player }) {
    this.eventBus = eventBus;
    this.config = config;
    this.player = player;
    this.boss = null;
    this._phase = -1;
    this._picksThisPhase = 0;
    this._pending = null; // the hand currently on offer
    this._burnTimer = 0;

    this._onPick = ({ id }) => this._resolve(id);
    eventBus.on('verse:pick', this._onPick);
  }

  get picks() {
    return this.boss?.versePicks ?? new Set();
  }

  /** How far in the arena's shortest half-extent the `narrow` pick has closed. */
  get radius() {
    const full = Math.min(ARENA.width, ARENA.height) / 2;
    if (!this.picks.has('narrow')) return full;
    return full * this.config.narrowFraction;
  }

  update(dt, boss) {
    this.boss = boss;
    if (!boss || boss.isDying) return;
    if (!boss.versePicks) boss.versePicks = new Set();

    if (boss.phase !== this._phase) {
      this._phase = boss.phase;
      this._picksThisPhase = 0;
    }
    // The opening phase is the fight introducing itself; the pen comes out at
    // the first escalation, which is what makes taking it feel like a turn.
    if (!this._pending && this._phase > 0 && this._picksThisPhase < this.config.picksPerPhase) {
      this._offer();
    }

    if (this.picks.has('narrow')) this._burnOutside(dt);
  }

  _offer() {
    const taken = this.picks;
    const available = Object.values(CARDS).filter((card) => !taken.has(card.id));
    const hand = [];
    // Shuffle-draw up to handSize; a fight that has taken everything gets the
    // un-writing card instead, which is the endgame the bible asks for.
    const pool = [...available];
    while (hand.length < this.config.handSize && pool.length) {
      hand.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
    }
    if (taken.size > 0 && hand.length < this.config.handSize) {
      const victim = [...taken][0];
      hand.push({
        id: `unwrite:${victim}`,
        label: 'Strike a Line',
        trade: `Removes "${CARDS[victim]?.label ?? victim}" — and it will ask again.`,
      });
    }
    if (!hand.length) return;
    this._pending = hand;
    this.eventBus.emit('verse:offer', { cards: hand, phase: this._phase });
  }

  _resolve(id) {
    if (!this._pending) return;
    const hand = this._pending;
    this._pending = null;
    this._picksThisPhase++;
    const taken = this.boss?.versePicks ?? new Set();

    if (!id) {
      // Declined. Refusal is a choice of verse too, and the worst one: the
      // fight takes the card the player most conspicuously didn't.
      const forced = hand.find((card) => !card.id.startsWith('unwrite:'));
      if (forced) taken.add(forced.id);
      this.eventBus.emit('verse:picked', { id: forced?.id ?? null, declined: true });
      return;
    }
    if (id.startsWith('unwrite:')) {
      taken.delete(id.slice('unwrite:'.length));
      // Un-writing costs a turn, not a phase: it removes and then the fight
      // immediately asks again, which is the "at a price" in the bible's spec.
      this._picksThisPhase--;
    } else {
      taken.add(id);
    }
    this.eventBus.emit('verse:picked', { id, declined: false });
  }

  /** The `narrow` pick: outside the shrunken floor, the ground burns. */
  _burnOutside(dt) {
    this._burnTimer -= dt;
    const cx = ARENA.x + ARENA.width / 2;
    const cy = ARENA.y + ARENA.height / 2;
    const outside = Math.hypot(this.player.x - cx, this.player.y - cy) > this.radius;
    if (outside && this._burnTimer <= 0 && !this.player.isInvincible) {
      this.player.takeDamage(this.config.narrowTickDamage, 'Tighter Margins');
      this._burnTimer = 0.5;
    }
  }

  getVisual() {
    return {
      narrowed: this.picks.has('narrow'),
      radius: this.radius,
      x: ARENA.x + ARENA.width / 2,
      y: ARENA.y + ARENA.height / 2,
      // The HUD readout: what the player has written so far.
      picks: [...this.picks].map((id) => CARDS[id]?.label ?? id),
    };
  }

  dispose() {
    this.eventBus.off('verse:pick', this._onPick);
    if (this._pending) {
      this._pending = null;
      // A fight that ends mid-offer must not leave the picker on screen.
      this.eventBus.emit('verse:picked', { id: null, declined: true });
    }
  }
}
